import { create } from 'zustand';
import api from '../utils/api';
import { homePathForUser, usesAdminWorkspace, usesSalesWorkspace } from '../utils/workspace';

/**
 * Maps a user's onboardingStep → which route they should be at.
 * Used by both Login/Signup redirects and the PrivateRoute gate so a
 * half-onboarded user is funneled back to the right step on every reload.
 */
export function routeForStep(user) {
    if (!user) return '/login';
    switch (user.onboardingStep) {
        case 'mail_otp':       return '/onboarding/verify-email';
        case 'admin_verify':   return '/onboarding/pending-approval';
        case 'kra_accept':     return '/onboarding/kra';
        case 'kpi_accept':     return '/onboarding/kpi';
        case 'training':       return '/onboarding/training';
        case 'welcome':        return '/onboarding/welcome';
        case 'complete':
        default:               return homePathForUser(user);
    }
}

const _savedImpersonation = (() => {
    try { return JSON.parse(localStorage.getItem('ageon_impersonation') || 'null'); } catch { return null; }
})();

export const useAuth = create((set, get) => ({
    user: null,
    token: localStorage.getItem('ageon_token') || null,
    loading: false,
    bootError: null,
    impersonation: _savedImpersonation ? { name: _savedImpersonation.user?.name, email: _savedImpersonation.user?.email } : null,

    async login(email, password) {
        set({ loading: true, bootError: null });
        try {
            const { data } = await api.post('/auth/login', { email, password });
            localStorage.setItem('ageon_token', data.token);
            set({ user: data.user, token: data.token, loading: false });
            return data.user;
        } catch (e) {
            set({ loading: false });
            throw e;
        }
    },

    /** Signup returns BOTH token + user, so we sign the new user in
        immediately and move them into the onboarding shell. */
    async signup(payload) {
        set({ loading: true });
        try {
            const { data } = await api.post('/auth/signup', payload);
            if (data.token) {
                localStorage.setItem('ageon_token', data.token);
                set({ user: data.user, token: data.token, loading: false });
            } else {
                set({ loading: false });
            }
            return data;
        } catch (e) {
            set({ loading: false });
            throw e;
        }
    },

    /** Resend the 6-digit OTP. */
    async sendOtp() {
        const { data } = await api.post('/auth/send-otp');
        return data;
    },

    /** Verify the 6-digit OTP. On success the user object's onboardingStep
        advances to 'admin_verify' — caller should refresh state. */
    async verifyOtp(code) {
        const { data } = await api.post('/auth/verify-otp', { code });
        set({ user: data.user });
        return data;
    },

    async loadMe() {
        if (!get().token) return null;
        try {
            const { data } = await api.get('/auth/me');
            set({ user: data.user });
            return data.user;
        } catch {
            localStorage.removeItem('ageon_token');
            set({ user: null, token: null });
            return null;
        }
    },

    async logout() {
        try {
            await api.post('/auth/logout');
        } catch { /* best-effort before clearing session */ }
        localStorage.removeItem('ageon_token');
        localStorage.removeItem('ageon_impersonation');
        set({ user: null, token: null, impersonation: null });
        window.location.href = '/login';
    },

    /** Admin impersonates another user. Stashes the admin session and swaps in the target token. */
    async startImpersonation(userId) {
        const { data } = await api.post(`/users/${userId}/impersonate`);
        // Save admin session
        const adminSession = { token: get().token, user: get().user };
        localStorage.setItem('ageon_impersonation', JSON.stringify(adminSession));
        localStorage.setItem('ageon_token', data.token);
        set({ token: data.token, user: data.user, impersonation: { ...data.impersonatedBy, originalToken: adminSession.token } });
        return data;
    },

    /** Exit impersonation — restore the admin session. */
    stopImpersonation() {
        const raw = localStorage.getItem('ageon_impersonation');
        if (!raw) return;
        try {
            const adminSession = JSON.parse(raw);
            localStorage.setItem('ageon_token', adminSession.token);
            localStorage.removeItem('ageon_impersonation');
            set({ token: adminSession.token, user: adminSession.user, impersonation: null });
            window.location.href = '/admin/users';
        } catch {
            localStorage.removeItem('ageon_impersonation');
            window.location.href = '/login';
        }
    },

    /** True when the current session is an admin impersonating someone. */
    isImpersonating() {
        return !!get().impersonation || !!localStorage.getItem('ageon_impersonation');
    },

    isAdmin() { return usesAdminWorkspace(get().user); },
    isSales() { return usesSalesWorkspace(get().user); },
    isOnboarded() { return get().user?.onboardingStep === 'complete'; },
    hasRole(...roles) { return roles.includes(get().user?.role); },
}));
